import { useState } from 'react';
import { API_BASE_URL } from '../config';
import ConfirmModal from './ConfirmModal';

function AltTextInput({ itemId, fieldSlug, onUpdate }: { itemId: string, fieldSlug: string, onUpdate: (itemId: string, fieldSlug: string, text: string) => Promise<boolean> }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'error' | null>(null);

  return (
    <div className="flex items-center gap-2 mt-1.5 ml-4">
      <input 
        aria-label="Alternative text"
        type="text" 
        value={text} 
        onChange={e => setText(e.target.value)} 
        placeholder="Enter alt text..."
        disabled={loading}
        className="bg-[var(--background3)] border border-[var(--border2)] text-[var(--text1)] text-[10px] md:text-[11px] px-2 py-1 rounded-[2px] focus:outline-none focus:border-[var(--blueBorder)]"
      />
      <button aria-label="Update alternative text" onClick={async () => { 
          if(text) { 
            setLoading(true);
            setStatus(null);
            const success = await onUpdate(itemId, fieldSlug, text);
            setLoading(false);
            if (!success) setStatus('error');
          }
        }}
        className="bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)] text-[var(--actionPrimaryText)] text-[10px] md:text-[11px] font-medium px-2 py-1 rounded-[2px] transition disabled:opacity-50"
        disabled={!text || loading}
      >
        {loading ? 'Updating...' : 'Update'}
      </button>
      {status === 'error' && <span className="text-[var(--redBackground)] text-[10px] md:text-[11px]">Try again</span>}
    </div>
  );
}

function ImageUploadFix({ onUpload, isFailed }: { onUpload: (file: File, altText?: string) => Promise<boolean>, isFailed: boolean }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'error' | null>(isFailed ? 'error' : null);

  return (
    <div className="mt-1.5 ml-4 flex flex-wrap items-center gap-2">
      <label aria-label="Upload replacement image" className={`cursor-pointer inline-block ${loading ? 'bg-[#555] cursor-not-allowed' : 'bg-[var(--background3)] hover:bg-[var(--background4)]'} border border-[var(--border2)] text-[var(--text1)] text-[10px] md:text-[11px] font-medium px-2 py-1 rounded-[2px] transition`}>
        {loading ? 'Uploading...' : 'Replace Image'}
        <input 
          type="file" 
          className="hidden" 
          accept="image/*"
          disabled={loading}
          onChange={async (e) => {
            if (e.target.files && e.target.files[0]) {
              setLoading(true);
              setStatus(null);
              const success = await onUpload(e.target.files[0]);
              setLoading(false);
              if (!success) setStatus('error');
            }
          }} 
        />
      </label>
      {status === 'error' && <span className="text-[var(--redBackground)] text-[10px] md:text-[11px]">Try again</span>}
    </div>
  );
}

function AutoFixButton({ onFix, label = 'Auto-Fix', isFailed }: { onFix: () => Promise<boolean>, label?: string, isFailed: boolean }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'error' | null>(isFailed ? 'error' : null);

  return (
    <div className="mt-1.5 ml-4 flex items-center gap-2">
      <button aria-label={label} onClick={async () => {
          setLoading(true);
          setStatus(null);
          const success = await onFix();
          setLoading(false);
          if (!success) setStatus('error');
        }}
        disabled={loading}
        className="bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)] text-[var(--actionPrimaryText)] text-[10px] md:text-[11px] font-medium px-2 py-1 rounded-[2px] transition disabled:opacity-50"
      >
        {loading ? 'Fixing...' : label}
      </button>
      {status === 'error' && <span className="text-[var(--redBackground)] text-[10px] md:text-[11px]">Try again</span>}
    </div>
  );
}

interface AuditResult {
  itemId: string;
  itemName: string;
  fieldSlug: string;
  ruleType: string;
  error: string;
}

interface AuditListProps {
  collectionsData: Record<string, { name: string, results: AuditResult[], inspectedAt?: number, meta?: any }>;
  isPremium?: boolean;
}

export default function AuditList({ collectionsData, token, siteId, onFixed, isPremium }: AuditListProps & { token?: string, siteId?: string, onFixed?: (collectionId: string, itemId: string, fieldSlug: string, ruleType: string) => void }) {
  const [expandedCollections, setExpandedCollections] = useState<Record<string, boolean>>(() => {
    // Expand all by default
    const initialState: Record<string, boolean> = {};
    Object.keys(collectionsData).forEach(id => initialState[id] = true);
    return initialState;
  });

  const [fixedItems, setFixedItems] = useState<Set<string>>(new Set());
  const [failedItems, setFailedItems] = useState<Set<string>>(new Set());
  const [confirmAction, setConfirmAction] = useState<{
    isOpen: boolean;
    title?: string;
    message?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
  }>({ isOpen: false });

  const handleUpload = async (file: File, collectionId: string, itemId: string, fieldSlug: string, ruleType: string, altText?: string) => {
    if (!token || !siteId || !onFixed) return false;
    const itemKey = `${itemId}-${fieldSlug}-${ruleType}`;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('collectionId', collectionId);
    formData.append('itemId', itemId);
    formData.append('fieldSlug', fieldSlug);
    formData.append('siteId', siteId);
    if (altText) {
      formData.append('altText', altText);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const res = await fetch(`${API_BASE_URL}/api/fix/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        setFixedItems(prev => new Set(prev).add(itemKey));
        setFailedItems(prev => { const s = new Set(prev); s.delete(itemKey); return s; });
        setTimeout(() => {
          onFixed(collectionId, itemId, fieldSlug, ruleType);
        }, 2000);
        return true;
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (errorData.error) {
           alert(errorData.error);
        }
        setFailedItems(prev => new Set(prev).add(itemKey));
        return false;
      }
    } catch (err) {
      console.error(err);
      setFailedItems(prev => new Set(prev).add(itemKey));
      return false;
    }
  };

  const handleUpdateAltText = async (collectionId: string, itemId: string, fieldSlug: string, text: string, ruleType: string) => {
    if (!token || !siteId || !onFixed) return false;
    const itemKey = `${itemId}-${fieldSlug}-${ruleType}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const res = await fetch(`${API_BASE_URL}/api/fix/alt-text`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ collectionId, itemId, fieldSlug, altText: text }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        setFixedItems(prev => new Set(prev).add(itemKey));
        setFailedItems(prev => { const s = new Set(prev); s.delete(itemKey); return s; });
        setTimeout(() => {
          onFixed(collectionId, itemId, fieldSlug, ruleType);
        }, 2000);
        return true;
      } else {
        setFailedItems(prev => new Set(prev).add(itemKey));
        return false;
      }
    } catch (err) {
      console.error(err);
      setFailedItems(prev => new Set(prev).add(itemKey));
      return false;
    }
  };

  const handleAutoFixText = async (collectionId: string, itemId: string, fieldSlug: string, ruleType: string, ruleValue?: string) => {
    if (!token || !siteId || !onFixed) return false;
    const itemKey = `${itemId}-${fieldSlug}-${ruleType}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const res = await fetch(`${API_BASE_URL}/api/fix/text`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ collectionId, itemId, fieldSlug, ruleType, ruleValue }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        setFixedItems(prev => new Set(prev).add(itemKey));
        setFailedItems(prev => { const s = new Set(prev); s.delete(itemKey); return s; });
        setTimeout(() => onFixed(collectionId, itemId, fieldSlug, ruleType), 2000);
        return true;
      } else {
        setFailedItems(prev => new Set(prev).add(itemKey));
        return false;
      }
    } catch (err) {
      setFailedItems(prev => new Set(prev).add(itemKey));
      return false;
    }
  };

  const handleAutoFixBrokenLink = async (collectionId: string, itemId: string, fieldSlug: string) => {
    if (!token || !siteId || !onFixed) return false;
    const itemKey = `${itemId}-${fieldSlug}-BROKEN_LINKS`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const res = await fetch(`${API_BASE_URL}/api/fix/broken-link`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ collectionId, itemId, fieldSlug }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        setFixedItems(prev => new Set(prev).add(itemKey));
        setFailedItems(prev => { const s = new Set(prev); s.delete(itemKey); return s; });
        setTimeout(() => onFixed(collectionId, itemId, fieldSlug, 'BROKEN_LINKS'), 2000);
        return true;
      } else {
        setFailedItems(prev => new Set(prev).add(itemKey));
        return false;
      }
    } catch (err) {
      setFailedItems(prev => new Set(prev).add(itemKey));
      return false;
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[14px] md:text-[15px] font-medium text-[var(--text1)] flex items-center">
          <svg className="w-4 h-4 mr-1.5 text-[var(--redText)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          Audit Results
        </h3>
      </div>
      
      {Object.keys(collectionsData).length === 0 || Object.values(collectionsData).every((c: any) => c.results.length === 0) ? (
        <div className="flex flex-col gap-3">
          <p className="text-[var(--greenText)] text-[12px] md:text-[13px] font-medium">All items passed the inspection!</p>
          {Object.values(collectionsData).map((c: any, i: number) => c.meta?.isLimited && !isPremium && (
            <div key={i} className="p-2.5 bg-[var(--yellowBackground)]/80 border border-[var(--yellowBorder)] rounded flex flex-col gap-1.5">
              <p className="text-[var(--yellowText)] text-[11px] md:text-[12px] font-medium flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Limited to {c.meta.inspectedItems} of {c.meta.totalItems} items
              </p>
              <p className="text-[var(--yellowText)] text-[10px] md:text-[11px]">Your free plan only analyzed the first {c.meta.inspectedItems} items. There might be hidden issues in the other items. Upgrade to scan the entire collection.</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.values(collectionsData).some((c: any) => c.meta?.isLimited) && !isPremium && (
            <div className="mb-2 space-y-2">
              {Object.values(collectionsData).map((c: any, i: number) => c.meta?.isLimited && (
                <div key={`warn-${i}`} className="p-2.5 bg-[var(--yellowBackground)]/80 border border-[var(--yellowBorder)] rounded flex flex-col gap-1.5">
                  <p className="text-[var(--yellowText)] text-[11px] md:text-[12px] font-medium flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    Limited to {c.meta.inspectedItems} of {c.meta.totalItems} items
                  </p>
                  <p className="text-[var(--yellowText)] text-[10px] md:text-[11px]">Your free plan only analyzed the first {c.meta.inspectedItems} items. Upgrade to scan the entire collection and find hidden issues.</p>
                </div>
              ))}
            </div>
          )}
          {Object.entries(collectionsData)
            .sort(([, a], [, b]) => (b.inspectedAt || 0) - (a.inspectedAt || 0))
            .map(([collectionId, { name, results }]) => {
            if (results.length === 0) return null;

            // Group results by itemId for this collection
            const groupedResults = results.reduce((acc, curr) => {
              if (!acc[curr.itemId]) {
                acc[curr.itemId] = {
                  itemName: curr.itemName,
                  errors: []
                };
              }
              acc[curr.itemId].errors.push(curr);
              return acc;
            }, {} as Record<string, { itemName: string; errors: any[] }>);

            const isExpanded = expandedCollections[collectionId] !== false;

            return (
              <div key={collectionId} className="border border-[var(--border1)] rounded-[4px] overflow-hidden">
                <button 
                  aria-expanded={isExpanded} className="w-full flex justify-between items-center bg-[var(--background2)] hover:bg-[var(--background3)] p-3 text-left transition"
                  onClick={() => setExpandedCollections(prev => ({ ...prev, [collectionId]: !isExpanded }))}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] md:text-[14px] font-semibold text-[var(--text1)]">{name}</span>
                    <span className="text-[11px] md:text-[12px] bg-[var(--redBackground)] text-white px-1.5 py-0.5 rounded-sm border border-[var(--redBorder)]">
                      {results.length} {results.length === 1 ? 'issue' : 'issues'} found
                    </span>
                  </div>
                  <svg 
                    className={`w-4 h-4 text-[var(--text3)] transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isExpanded && (
                  <div className="p-3 bg-[var(--background1)] space-y-3 border-t border-[var(--border1)]">
                    {Object.entries(groupedResults).map(([itemId, data]) => (
                      <div key={itemId} className="bg-[var(--background2)] border border-[var(--border1)] rounded-[3px] overflow-hidden">
                        <div className="bg-[var(--background3)] px-3 py-2 border-b border-[var(--border2)] flex flex-row justify-between items-center gap-2">
                          <span className="font-semibold text-[var(--text1)] text-[12px] md:text-[13px] truncate">{data.itemName}</span>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <span className="text-[10px] md:text-[11px] text-[var(--textInactive)] font-mono bg-[var(--background4)] px-1.5 py-0.5 rounded-[2px]">ID: {itemId.slice(-6)}</span>
                          </div>
                        </div>
                        
                        <ul className="p-3 space-y-2">
                          {data.errors.map((errorObj, idx) => {
                            const itemKey = `${itemId}-${errorObj.fieldSlug}-${errorObj.ruleType}`;
                            const isFixed = fixedItems.has(itemKey);
                            const isFailed = failedItems.has(itemKey);

                            return (
                            <li key={idx} className={`flex flex-col text-[11px] md:text-[12px] transition-colors duration-500 ${isFixed ? 'text-[var(--greenBackgroundHover)]' : 'text-[var(--redBackground)]'}`}>
                              <div className="flex items-start">
                                <span className="mr-1.5 mt-0.5">•</span>
                                <span>{isFixed ? 'Successfully updated Webflow CMS item!' : errorObj.error}</span>
                              </div>
                              {!isFixed && (errorObj.ruleType === 'MAX_IMAGE_SIZE' || errorObj.ruleType === 'ALLOWED_IMAGE_FORMATS') && errorObj.fieldSlug && (
                                <ImageUploadFix 
                                  isFailed={isFailed}
                                  onUpload={async (file, altText) => {
                                    return await handleUpload(file, collectionId, itemId, errorObj.fieldSlug, errorObj.ruleType, altText);
                                  }}
                                />
                              )}
                              {!isFixed && errorObj.ruleType === 'MISSING_ALT_TEXT' && errorObj.fieldSlug && (
                                <AltTextInput itemId={itemId} fieldSlug={errorObj.fieldSlug} onUpdate={(iid, fs, text) => handleUpdateAltText(collectionId, iid, fs, text, errorObj.ruleType)} />
                              )}
                              {!isFixed && (errorObj.ruleType === 'TRAILING_SPACES' || errorObj.ruleType === 'TEXT_CASING') && errorObj.fieldSlug && (
                                <AutoFixButton 
                                  isFailed={isFailed}
                                  label="Auto-Fix Text"
                                  onFix={() => handleAutoFixText(collectionId, itemId, errorObj.fieldSlug, errorObj.ruleType, errorObj.ruleValue)}
                                />
                              )}
                              {!isFixed && errorObj.ruleType === 'BROKEN_LINKS' && errorObj.fieldSlug && (
                                <AutoFixButton 
                                  isFailed={isFailed}
                                  label="Remove Broken Links"
                                  onFix={() => {
                                    return new Promise<boolean>((resolve) => {
                                      setConfirmAction({
                                        isOpen: true,
                                        title: 'Remove Broken Link',
                                        message: 'Are you sure you want to remove this broken link? This will modify the CMS item directly.',
                                        onConfirm: async () => {
                                          setConfirmAction({ isOpen: false });
                                          const success = await handleAutoFixBrokenLink(collectionId, itemId, errorObj.fieldSlug);
                                          resolve(success);
                                        },
                                        onCancel: () => {
                                          setConfirmAction({ isOpen: false });
                                          resolve(true); // Treat cancel as success to avoid error toast
                                        }
                                      });
                                    });
                                  }}
                                />
                              )}
                            </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        isOpen={confirmAction.isOpen}
        title={confirmAction.title || ''}
        message={confirmAction.message || ''}
        onConfirm={confirmAction.onConfirm || (() => {})}
        onCancel={confirmAction.onCancel || (() => {})}
        confirmText="Remove Link"
      />
    </div>
  );
}

